/**
 * Vault catalog rows — fixed-layer thumbnail preview (bottom-left anchor).
 * CSS overflow/z-index cannot escape catalog sidebar grid; Shopify Horizon has no built-in row popover zoom.
 */
(function () {
  const THUMB = 64;
  const BASE_HOVER = 192;
  const HOVER = BASE_HOVER * 4;
  const POP_ID = 'uucck-vault-thumb-popover';

  function enabled() {
    const body = document.body;
    if (!body.classList.contains('template-index') && !body.classList.contains('template-collection')) {
      return false;
    }
    return window.matchMedia('(min-width: 750px) and (any-pointer: fine)').matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** @type {HTMLElement | null} */
  let popover = null;
  /** @type {HTMLElement | null} */
  let activeGallery = null;

  /** @type {WeakSet<HTMLElement>} */
  const boundGalleries = new WeakSet();

  function ensurePopover() {
    if (popover) return popover;
    popover = document.createElement('div');
    popover.id = POP_ID;
    popover.className = 'uucck-vault-thumb-popover';
    popover.setAttribute('aria-hidden', 'true');
    const img = document.createElement('img');
    img.decoding = 'async';
    img.alt = '';
    popover.appendChild(img);
    document.body.appendChild(popover);
    return popover;
  }

  /** @param {HTMLElement} gallery */
  function thumbImg(gallery) {
    const img = gallery.querySelector('img');
    return img instanceof HTMLImageElement ? img : null;
  }

  /** @param {HTMLElement} gallery */
  function show(gallery) {
    if (!enabled() || prefersReducedMotion()) return;
    const srcImg = thumbImg(gallery);
    if (!srcImg) return;

    const pop = ensurePopover();
    const popImg = pop.querySelector('img');
    if (!(popImg instanceof HTMLImageElement)) return;

    const src = srcImg.currentSrc || srcImg.src;
    if (!src) return;

    if (popImg.src !== src) {
      popImg.src = src;
      popImg.srcset = srcImg.srcset || '';
      popImg.sizes = srcImg.sizes || '';
    }
    popImg.alt = srcImg.alt || '';

    const rect = gallery.getBoundingClientRect();
    const pad = 8;
    const maxW = window.innerWidth - pad * 2;
    const maxH = window.innerHeight - pad * 2;
    const size = Math.min(HOVER, maxW, maxH);

    /* Bottom-left of preview aligns with bottom-left of 64px thumb slot */
    let left = rect.left;
    let top = rect.bottom - size;

    if (left + size > window.innerWidth - pad) left = window.innerWidth - size - pad;
    if (left < pad) left = pad;
    if (top < pad) top = pad;

    pop.style.width = `${size}px`;
    pop.style.height = `${size}px`;
    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;

    pop.classList.add('is-visible');
    pop.setAttribute('aria-hidden', 'false');
    activeGallery = gallery;
  }

  function hide() {
    if (!popover) return;
    popover.classList.remove('is-visible');
    popover.setAttribute('aria-hidden', 'true');
    activeGallery = null;
  }

  function reposition() {
    if (activeGallery) show(activeGallery);
  }

  /** @param {HTMLElement} gallery */
  function bindGallery(gallery) {
    if (boundGalleries.has(gallery)) return;
    boundGalleries.add(gallery);

    gallery.addEventListener('pointerenter', () => show(gallery));
    gallery.addEventListener('pointerleave', (e) => {
      if (e.relatedTarget instanceof Node && gallery.contains(e.relatedTarget)) return;
      hide();
    });
    gallery.addEventListener('focusin', () => show(gallery));
    gallery.addEventListener('focusout', (e) => {
      if (e.relatedTarget instanceof Node && gallery.contains(e.relatedTarget)) return;
      hide();
    });
  }

  function scan() {
    if (!enabled()) {
      hide();
      return;
    }
    document.documentElement.classList.add('uucck-vault-thumb-popover-active');
    document.querySelectorAll('product-card .card-gallery').forEach((node) => {
      const gallery = /** @type {HTMLElement} */ (node);
      const card = gallery.closest('product-card');
      if (!card?.querySelector('.uucck-vault-catalog-actions')) return;
      bindGallery(gallery);
    });
  }

  function init() {
    if (!document.body.classList.contains('template-index') && !document.body.classList.contains('template-collection')) {
      return;
    }
    scan();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    window.matchMedia('(min-width: 750px)').addEventListener('change', () => {
      hide();
      scan();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => {
    hide();
    scan();
  });

  document.addEventListener('uucck:collection-listing-updated', () => {
    hide();
    scan();
  });
})();
